import { load, type ReCaptchaInstance } from 'recaptcha-v3';
import Bugsnag from '@bugsnag/js';

const SITE_KEY = '6LeowvAdAAAAAFTChiD9pWNmWmt7uTyMRpncRGya';
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/**
 * Shown when no token could be obtained at all - the script was blocked or
 * Google is unreachable (TPL-3489) - which is a network problem on the user's
 * side, not a failed check, so say what they can do about it.
 */
export const RECAPTCHA_UNAVAILABLE_MESSAGE =
    "Captcha couldn't load. Check your internet connection or turn off your ad blocker for this site, then try again.";

/** The same, short enough to sit under a form field. */
export const RECAPTCHA_UNAVAILABLE_FIELD_MESSAGE = "Captcha couldn't load.";

let instancePromise: Promise<ReCaptchaInstance> | null = null;

function loadInstance(): Promise<ReCaptchaInstance> {
    if (!instancePromise) {
        instancePromise = load(SITE_KEY, {
            autoHideBadge: true,
        }).catch((error) => {
            // Reset so next call can retry
            instancePromise = null;
            throw error;
        });
    }
    return instancePromise;
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useRecaptchaToken() {
    const getRecaptchaToken = async (action: string): Promise<string> => {
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                const instance = await loadInstance();
                const token = await instance.execute(action);
                if (token) {
                    return token;
                }
            } catch {
                // Reset cached promise so next attempt tries fresh
                instancePromise = null;

                if (attempt < MAX_RETRIES) {
                    await delay(RETRY_DELAY_MS);
                }
            }
        }

        Bugsnag.notify(
            new Error(
                `ReCaptcha token retrieval failed after ${MAX_RETRIES + 1} attempts for action: ${action}`
            )
        );

        return '';
    };

    return { getRecaptchaToken };
}
